
import { useState } from 'react';

interface Order {
  id: string;
  created_at: string;
  product_name: string;
  product_brand: string;
  product_category: string;
  product_price: number;
  commission_rate: number;
  commission_amount: number;
  customer_email: string;
  customer_name: string;
  payment_status: string;
  order_status: string;
  shipping_address?: {
    city?: string;
    country?: string;
    line1?: string;
    state?: string;
    postal_code?: string;
  };
}

interface Props {
  orders: Order[];
  onRefresh: () => void;
  isLive: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  processing: 'bg-amber-100 text-amber-700',
  pending: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-rose-100 text-rose-700',
};

const STATUS_ICONS: Record<string, string> = {
  completed: 'ri-checkbox-circle-fill',
  processing: 'ri-loader-3-line',
  pending: 'ri-time-line',
  failed: 'ri-close-circle-fill',
  refunded: 'ri-refund-2-line',
};

export default function OrdersTable({ orders, onRefresh, isLive }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'created_at' | 'product_price' | 'commission_amount'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = orders
    .filter((o) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        o.product_name?.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_email?.toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || o.payment_status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'created_at')
        return mul * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return mul * (Number(a[sortField]) - Number(b[sortField]));
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const SortIcon = ({ field }: { field: typeof sortField }) => (
    <i
      className={`ml-1 text-xs ${
        sortField === field ? 'text-teal-500' : 'text-gray-300'
      } ${
        sortField === field && sortDir === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
      }`}
    ></i>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Toolbar */}
      <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <div
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
              isLive ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isLive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
              }`}
            ></span>
            {isLive ? 'Live' : 'Offline'}
          </div>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-teal-600 px-3 py-1.5 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            Refresh
          </button>
          <span className="text-xs text-gray-400">{filtered.length} orders</span>
        </div>
      </div>

      {/* Table */}
      {paginated.length === 0 ? (
        <div className="text-center py-20">
          <i className="ri-inbox-line text-5xl text-gray-200 mb-3"></i>
          <p className="text-gray-400 text-sm">No orders found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('created_at')}
                    className="flex items-center cursor-pointer hover:text-gray-700 whitespace-nowrap"
                  >
                    Date <SortIcon field="created_at" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('product_price')}
                    className="flex items-center cursor-pointer hover:text-gray-700 whitespace-nowrap"
                  >
                    Price <SortIcon field="product_price" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('commission_amount')}
                    className="flex items-center cursor-pointer hover:text-gray-700 whitespace-nowrap"
                  >
                    Commission <SortIcon field="commission_amount" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginated.map((order) => (
                <React.Fragment key={order.id}>
                  <tr
                    className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 truncate max-w-[180px]">
                        {order.product_name || '—'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {order.product_brand} · {order.product_category}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-800">{order.customer_name || '—'}</div>
                      <div className="text-xs text-gray-400 truncate max-w-[160px]">
                        {order.customer_email || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">
                      ${Number(order.product_price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-emerald-600">
                        ${Number(order.commission_amount).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-400">{order.commission_rate}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          STATUS_STYLES[order.payment_status] || STATUS_STYLES.pending
                        }`}
                      >
                        <i
                          className={`${
                            STATUS_ICONS[order.payment_status] || STATUS_ICONS.pending
                          } text-xs`}
                        ></i>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <i
                        className={`text-gray-400 transition-transform ${
                          expandedId === order.id ? 'ri-arrow-up-s-line rotate-0' : 'ri-arrow-down-s-line'
                        }`}
                      ></i>
                    </td>
                  </tr>
                  {expandedId === order.id && (
                    <tr className="bg-teal-50/30">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">
                              Order ID
                            </div>
                            <div className="font-mono text-gray-700 text-xs break-all">{order.id}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">
                              Order Status
                            </div>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                STATUS_STYLES[order.order_status] || STATUS_STYLES.pending
                              }`}
                            >
                              {order.order_status || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">
                              Shipping Address
                            </div>
                            <div className="text-gray-700 text-xs">
                              {order.shipping_address
                                ? `${order.shipping_address.line1 || ''}, ${order.shipping_address.city || ''}, ${order.shipping_address.state || ''} ${order.shipping_address.postal_code || ''}, ${order.shipping_address.country || ''}`
                                : 'Not provided'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">
                              Commission Rate
                            </div>
                            <div className="text-gray-700">
                              {order.commission_rate}% →{' '}
                              <span className="text-emerald-600 font-semibold">
                                ${Number(order.commission_amount).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Page {page} of {totalPages} · {filtered.length} results
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                    p === page ? 'bg-teal-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
